import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { ManageBankFeedsLink } from "./ManageBankFeedsLink";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { sortPropertiesParentFirst } from "@/lib/pnl-columns";
import { PropertyFilterSelect, CategoryFilterSelect } from "./_filters";
import { useLocation, useSearch } from "wouter";
import { usePermissions } from "@/hooks/use-permissions";
import "./financials-contrast.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSearch, Plus, Trash2, Pencil, Copy, CheckCircle2, ChevronDown, ChevronRight, Building2, Undo2, Link2, ListChecks, X, Loader2, Eye, EyeOff, Zap, ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, Paperclip, Upload, Sparkles, Search, Scissors } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Property, Expense, ExpenseCategorySettings } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { DocumentUploadDialog } from "@/components/document-upload-dialog";
import { ManageDocumentDialog } from "@/components/manage-document-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ExpenseSourceDocumentTooltip } from "@/components/expense-source-document-tooltip";
import { ExpenseAttachmentDrawer } from "@/components/expense-attachment-drawer";
import { PaginationControls, clampPage } from "@/components/pagination-controls";
import { DateRangeFilter, computeDateRange, type DatePreset , ALL_DATE_PRESETS } from "@/components/DateRangeFilter";
import { ExpenseWithDoc, isMercurySource, isImportedBankSource, EXPENSE_CATEGORIES, getCategoryLabel, highlightMatch, fmtCurrency, HIGH_CONFIDENCE_OPTIONS, DEFAULT_AI_SUGGESTION_CONFIDENCE, AI_SUGGESTION_CONFIDENCE_OPTIONS, formatMercuryAccountLabel } from "./_shared";
import { MercuryMergeCandidatesPanel } from "./_MercuryMergePanel";
import { StatusTabs, deriveReviewStatus, type ReviewStatusFilter } from "./_StatusTabs";
import { FinancialsToolbar } from "./_FinancialsToolbar";
import { BulkBar } from "./_BulkBar";
import { InlineCellEditor } from "./_InlineCellEditor";
import { CategoryRulesDialog } from "./_CategoryRulesDialog";

// Revenue triage — valid values for the URL-persisted `status` param. The
// default (`for_review`) is never written to the URL, matching how the other
// expense filters treat their defaults.
const VALID_REVIEW_STATUSES: readonly ReviewStatusFilter[] = ["for_review", "categorized", "excluded", "all"];

const EXPENSE_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const EXPENSE_PAGE_SIZE_STORAGE_KEY = "expenses:pageSize";
const isValidExpensePageSize = (n: number): boolean =>
  (EXPENSE_PAGE_SIZE_OPTIONS as readonly number[]).includes(n);
const readStoredExpensePageSize = (): number => {
  if (typeof window === "undefined") return 50;
  try {
    const stored = parseInt(window.localStorage.getItem(EXPENSE_PAGE_SIZE_STORAGE_KEY) || "");
    return isValidExpensePageSize(stored) ? stored : 50;
  } catch {
    return 50;
  }
};


// Task #1938 — Default high-confidence cutoff when the host hasn't tuned the
// preference. Allowed picker values are kept in sync with the server-side
// validator in `server/routes/financials.ts`.
// Task #2165 — Per-host AI suggestion confidence threshold (separate from the
// Mercury merge threshold above). Lower values surface noisier suggestions,
// higher values keep only near-perfect picks. Default 85 matches the prior
// hard-coded constant from Task #2159.


// Task #2175 — Per-host AI category suggestion accuracy widget. Mirrors the
// admin panel introduced in Task #2167 but scoped to the calling host's own
// suggestion events so hosts can spot vendors where the AI keeps guessing
// wrong for *their* portfolio.
type HostAiSuggestionStats = {
  totals: { accepted: number; overridden: number; total: number };
  byCategory: Array<{ suggestedCategory: string; accepted: number; overridden: number; total: number }>;
  topOverriddenVendors: Array<{ vendorNormalized: string; suggestedCategory: string; overrideCount: number; topChosenCategory: string | null }>;
};

export function HostAiSuggestionAccuracyWidget() {
  const [expanded, setExpanded] = useState(false);
  const statsQuery = useQuery<HostAiSuggestionStats>({
    queryKey: ["/api/app/expense-ai-suggestion-stats"],
  });
  const data = statsQuery.data;
  const total = data?.totals.total ?? 0;
  const acceptRate = data && total > 0 ? Math.round((data.totals.accepted / total) * 100) : null;

  if (statsQuery.isLoading) {
    return (
      <div className="mb-4" data-testid="widget-ai-suggestion-accuracy-loading">
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (statsQuery.isError) return null;
  if (!data || total === 0) return null;

  return (
    <div className="financials-ai-accuracy mb-4 rounded-lg border" data-testid="widget-ai-suggestion-accuracy">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left rounded-lg"
        aria-expanded={expanded}
        data-testid="button-toggle-ai-suggestion-accuracy"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-violet-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-violet-900">AI suggestion accuracy</div>
            <div className="text-xs text-violet-700">
              You accepted <span className="font-semibold" data-testid="text-ai-accept-rate">{acceptRate}%</span> of {total} AI category suggestion{total === 1 ? "" : "s"}
              {" "}({data.totals.accepted} accepted, {data.totals.overridden} overridden).
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-violet-700 shrink-0" /> : <ChevronRight className="h-4 w-4 text-violet-700 shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-violet-200 pt-3">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Accept rate per suggested category</h4>
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Suggested category</th>
                    <th className="text-right px-3 py-2 font-medium">Accepted</th>
                    <th className="text-right px-3 py-2 font-medium">Overridden</th>
                    <th className="text-right px-3 py-2 font-medium">Total</th>
                    <th className="text-right px-3 py-2 font-medium">Accept rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCategory.map((row) => {
                    const rate = row.total > 0 ? Math.round((row.accepted / row.total) * 100) : 0;
                    return (
                      <tr key={row.suggestedCategory} className="border-t border-gray-100" data-testid={`row-host-ai-category-${row.suggestedCategory}`}>
                        <td className="px-3 py-2 font-medium text-gray-800">{row.suggestedCategory}</td>
                        <td className="px-3 py-2 text-right">{row.accepted}</td>
                        <td className="px-3 py-2 text-right">{row.overridden}</td>
                        <td className="px-3 py-2 text-right">{row.total}</td>
                        <td className="px-3 py-2 text-right" data-testid={`text-host-ai-rate-${row.suggestedCategory}`}>{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Vendors you most often re-categorize</h4>
            {data.topOverriddenVendors.length === 0 ? (
              <div className="text-sm text-gray-500">No vendor overrides yet.</div>
            ) : (
              <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Vendor</th>
                      <th className="text-left px-3 py-2 font-medium">AI suggested</th>
                      <th className="text-left px-3 py-2 font-medium">You usually pick</th>
                      <th className="text-right px-3 py-2 font-medium">Overrides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topOverriddenVendors.map((row) => (
                      <tr
                        key={`${row.vendorNormalized}::${row.suggestedCategory}`}
                        className="border-t border-gray-100"
                        data-testid={`row-host-ai-override-${row.vendorNormalized}`}
                      >
                        <td className="px-3 py-2 font-medium text-gray-800">{row.vendorNormalized}</td>
                        <td className="px-3 py-2 text-gray-700">{row.suggestedCategory}</td>
                        <td className="px-3 py-2 text-gray-700">{row.topChosenCategory ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{row.overrideCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ExpensesTab({ properties, initialPropertyId }: { properties: Property[]; initialPropertyId?: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("financials");
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadPropertyId, setUploadPropertyId] = useState<number | null>(null);
  const [manageDocId, setManageDocId] = useState<number | null>(null);
  type SortState = { col: string; dir: "asc" | "desc" } | null;
  const [sortState, setSortState] = useState<SortState>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkVendor, setBulkVendor] = useState<string>("");
  const [showBulkVendor, setShowBulkVendor] = useState(false);
  const [showCategoryRules, setShowCategoryRules] = useState(false);
  const [attachmentExpense, setAttachmentExpense] = useState<(Expense & { documentFileName?: string | null }) | null>(null);
  const [expenseSearchText, setExpenseSearchText] = useState("");

  const propertyFilter = searchParams.get("propertyId") || initialPropertyId || "all";
  const categoryFilter = searchParams.get("category") || "all";
  const sourceFilter = searchParams.get("source") || "all";
  const showDisabledMercury = searchParams.get("disabledMercury") === "1";
  // Revenue triage — QBO-style status tab, URL-persisted alongside the other
  // expense filters. Deep-links that used the old "jump to uncategorized"
  // params (category=other[&source=mercury]) carry no status param, so they
  // land on the default For review tab — the triage queue that superseded the
  // amber banners.
  const rawStatus = searchParams.get("status") || "";
  const statusFilter: ReviewStatusFilter = (VALID_REVIEW_STATUSES as readonly string[]).includes(rawStatus)
    ? (rawStatus as ReviewStatusFilter)
    : "for_review";
  const VALID_EXPENSE_DATE_PRESETS: readonly DatePreset[] = ALL_DATE_PRESETS;
  const rawExpDatePreset = searchParams.get("datePreset") || "";
  const datePreset = (VALID_EXPENSE_DATE_PRESETS.includes(rawExpDatePreset as DatePreset) ? rawExpDatePreset : "") as DatePreset;
  const dateFrom = searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("dateTo") || "";
  const rawPage = parseInt(searchParams.get("page") || "1");
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;
  const urlPageSizeParam = searchParams.get("pageSize");
  const rawPageSize = urlPageSizeParam !== null
    ? parseInt(urlPageSizeParam)
    : readStoredExpensePageSize();
  const pageSize = isValidExpensePageSize(rawPageSize) ? rawPageSize : 50;

  useEffect(() => {
    const initialParams = new URLSearchParams(window.location.search);
    const hasShareableFilters =
      initialParams.has("propertyId") ||
      initialParams.has("category") ||
      initialParams.has("status") ||
      initialParams.has("page") ||
      initialParams.has("pageSize") ||
      initialParams.has("datePreset") ||
      initialParams.has("dateFrom") ||
      initialParams.has("dateTo");
    if (hasShareableFilters) {
      toast({
        title: "Shared view loaded",
        description: "Filters from the link have been applied.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateExpensesQuery = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const setPropertyFilter = (v: string) => {
    updateExpensesQuery({ propertyId: v === "all" ? null : v });
  };
  const setCategoryFilter = (v: string) => {
    updateExpensesQuery({ category: v === "all" ? null : v });
  };
  const setSourceFilter = (v: string) => {
    updateExpensesQuery({ source: v === "all" ? null : v });
  };
  const setShowDisabledMercury = (v: boolean) => {
    updateExpensesQuery({ disabledMercury: v ? "1" : null });
  };
  // Revenue triage — switching tabs resets pagination and drops the selection
  // so bulk actions never silently touch rows hidden under another tab.
  const setStatusFilter = (v: ReviewStatusFilter) => {
    setSelectedIds(new Set());
    updateExpensesQuery({ status: v === "for_review" ? null : v, page: null });
  };
  const setDatePreset = (preset: DatePreset) => {
    const updates: Record<string, string | null> = {
      datePreset: preset || null,
      dateFrom: null,
      dateTo: null,
    };
    updateExpensesQuery(updates);
  };
  const setExpenseDateFrom = (v: string) => updateExpensesQuery({ dateFrom: v || null });
  const setExpenseDateTo = (v: string) => updateExpensesQuery({ dateTo: v || null });
  const setPage = (n: number) => {
    updateExpensesQuery({ page: n <= 1 ? null : String(n) });
  };
  const persistExpensePageSize = (size: number) => {
    if (!isValidExpensePageSize(size)) return;
    try {
      window.localStorage.setItem(EXPENSE_PAGE_SIZE_STORAGE_KEY, String(size));
    } catch {
      // ignore storage errors (e.g., private mode, quota exceeded)
    }
  };
  const setPageSize = (n: number) => {
    persistExpensePageSize(n);
    updateExpensesQuery({ pageSize: n === 50 ? null : String(n), page: null });
  };

  const handleCopyLink = async () => {
    const next = new URLSearchParams();
    if (propertyFilter !== "all") next.set("propertyId", propertyFilter);
    if (categoryFilter !== "all") next.set("category", categoryFilter);
    if (statusFilter !== "for_review") next.set("status", statusFilter);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 50) next.set("pageSize", String(pageSize));
    if (datePreset) next.set("datePreset", datePreset);
    if (datePreset === "custom" && dateFrom) next.set("dateFrom", dateFrom);
    if (datePreset === "custom" && dateTo) next.set("dateTo", dateTo);
    const qs = next.toString();
    const url = `${window.location.origin}${pathname}${qs ? `?${qs}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: "Share this URL to open the same view." });
    } catch {
      toast({
        title: "Couldn't copy link",
        description: "Copy this URL manually: " + url,
        variant: "destructive",
      });
    }
  };
  const [form, setForm] = useState({
    propertyId: "",
    category: "",
    description: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    vendor: "",
    notes: "",
  });
  // Task #2305 — split mode state
  const [splitMode, setSplitMode] = useState(false);
  const [splitLines, setSplitLines] = useState<Array<{ propertyId: string; category: string; amount: string; notes: string }>>([]);
  const [splitError, setSplitError] = useState<string | null>(null);

  // Revenue triage — voided/excluded rows are always fetched now: they no
  // longer hide behind "Show voided/excluded" toggles but live under the
  // Excluded status tab (which shows both).
  const expensesQueryKey = ["/api/app/expenses", "with-voided", "with-excluded", showDisabledMercury ? "with-disabled-mercury" : ""].filter(Boolean);
  const expensesQuery = useQuery<ExpenseWithDoc[]>({
    queryKey: expensesQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("includeVoided", "true");
      params.set("includeExcluded", "true");
      if (showDisabledMercury) params.set("includeDisabledMercury", "true");
      const url = `/api/app/expenses?${params.toString()}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load expenses");
      return res.json();
    },
  });
  const journeyStage = useJourneyStage();
  const catSettingsQuery = useQuery<ExpenseCategorySettings>({ queryKey: ["/api/app/expense-category-settings"] });
  const catSettings = catSettingsQuery.data;
  const availableCategories = useMemo(() => {
    const disabled = (catSettings?.disabledCategories as string[]) || [];
    const custom = (catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [];
    const builtIn = EXPENSE_CATEGORIES.filter((c) => !disabled.includes(c.value));
    return [...builtIn, ...custom];
  }, [catSettings]);

  // Task #2311 — balance sheet category values for visual grouping in dropdowns.
  const balanceSheetCategoryValues = useMemo(() => {
    const custom = (catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [];
    return new Set(custom.filter((c) => c.isBalanceSheet).map((c) => c.value));
  }, [catSettings]);

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => apiRequest("POST", "/api/app/expenses", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense added" });
      setShowAdd(false);
      resetForm();
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Couldn't add this expense. Please try again."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => apiRequest("PATCH", `/api/app/expenses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense updated" });
      setEditId(null);
      setShowAdd(false);
      resetForm();
    },
  });

  // Task #2305 — split expense mutations
  const upsertSplitMutation = useMutation({
    mutationFn: async ({ id, lines }: { id: number; lines: Array<{ propertyId?: number | null; category: string; amount: string; notes?: string | null }> }) =>
      apiRequest("POST", `/api/app/expenses/${id}/splits`, { lines }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Split saved" });
      setEditId(null);
      setShowAdd(false);
      resetForm();
    },
    onError: (e: Error) => toast({ title: "Split error", description: parseErrorMessage(e, "Couldn't save split."), variant: "destructive" }),
  });

  const deleteSplitMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/expenses/${id}/splits`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] }),
  });

  // Task #2314 — confirmation guard when host toggles Split OFF and saves,
  // so the existing split line items are not silently removed.
  const [unsplitConfirm, setUnsplitConfirm] = useState<
    { childCount: number; payload: Record<string, unknown> } | null
  >(null);

  // Task #2167 — Best-effort telemetry for AI category suggestion accept
  // vs. override. Fire-and-forget — never block the categorization flow if
  // logging fails.
  const recordAiSuggestionEvent = (
    expenseId: number,
    eventType: "accepted" | "overridden",
    chosenCategory?: string,
  ) => {
    apiRequest("POST", "/api/app/expense-ai-suggestion-events", {
      expenseId,
      eventType,
      ...(chosenCategory ? { chosenCategory } : {}),
    }).catch(() => {
      // Swallow — telemetry must never break categorization UX.
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/expenses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense deleted" });
    },
  });

  const excludeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/${id}/exclude`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense excluded", description: "This bank transaction is hidden from the active list and excluded from P&L." });
    },
    onError: (e: unknown) => {
      toast({ title: "Could not exclude", description: parseErrorMessage(e, "An error occurred."), variant: "destructive" });
    },
  });

  // Task #2135 — Bulk-exclude Mercury transactions. Reuses the shared
  // `selectedIds` selection state from Task #2104's bulk-edit UX so a single
  // checkbox column can drive both bulk-edit and bulk-exclude actions.
  const bulkExcludeMutation = useMutation<
    { excludedIds: number[]; skipped: { id: number; reason: string }[] },
    Error,
    number[]
  >({
    mutationFn: async (ids: number[]) => {
      const res = await apiRequest("POST", "/api/app/expenses/bulk-exclude", { ids });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", "with-voided"] });
      setSelectedIds(new Set());
      const excludedCount = data.excludedIds.length;
      const skippedCount = data.skipped.length;
      toast({
        title: `Excluded ${excludedCount} ${excludedCount === 1 ? "expense" : "expenses"}`,
        description: skippedCount > 0
          ? `${skippedCount} ${skippedCount === 1 ? "row was" : "rows were"} skipped (ineligible or already excluded).`
          : "These bank transactions are hidden from the active list and excluded from P&L.",
      });
    },
    onError: (e: unknown) => {
      toast({ title: "Could not exclude", description: parseErrorMessage(e, "An error occurred."), variant: "destructive" });
    },
  });

  const unexcludeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/expenses/${id}/unexclude`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense restored", description: "This transaction will appear in the active expense list." });
    },
    onError: (e: unknown) => {
      toast({ title: "Could not restore", description: parseErrorMessage(e, "An error occurred."), variant: "destructive" });
    },
  });

  // Task #2147 — Bulk-restore mirror of Task #2135's bulk-exclude. Lets hosts
  // select many excluded rows in the "Show excluded" view and un-exclude them
  // in one click instead of clicking the per-row restore button.
  const bulkUnexcludeMutation = useMutation<
    { restoredIds: number[]; skipped: { id: number; reason: string }[] },
    Error,
    number[]
  >({
    mutationFn: async (ids: number[]) => {
      const res = await apiRequest("POST", "/api/app/expenses/bulk-unexclude", { ids });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", "with-voided"] });
      setSelectedIds(new Set());
      const restoredCount = data.restoredIds.length;
      const skippedCount = data.skipped.length;
      toast({
        title: `Restored ${restoredCount} ${restoredCount === 1 ? "expense" : "expenses"}`,
        description: skippedCount > 0
          ? `${skippedCount} ${skippedCount === 1 ? "row was" : "rows were"} skipped (not excluded).`
          : "These transactions are back in the active expense list.",
      });
    },
    onError: (e: unknown) => {
      toast({ title: "Could not restore", description: parseErrorMessage(e, "An error occurred."), variant: "destructive" });
    },
  });

  const [accountFilter, setAccountFilter] = useState<string>("all");

  function resetForm() {
    setForm({ propertyId: "", category: "", description: "", amount: "", date: new Date().toISOString().split("T")[0], vendor: "", notes: "" });
    setIsDuplicating(false);
    setSplitMode(false);
    setSplitLines([]);
    setSplitError(null);
  }

  function openEdit(expense: Expense) {
    setEditId(expense.id);
    setForm({
      propertyId: (expense as any).isCompanyLevel ? "__company__" : (expense.propertyId ? String(expense.propertyId) : ""),
      category: expense.category,
      description: expense.description || "",
      amount: expense.amount,
      date: expense.date,
      vendor: expense.vendor || "",
      notes: expense.notes || "",
    });
    // Task #2305 — load split children when editing a split parent
    const children = (expensesQuery.data ?? []).filter((e) => (e as any).splitParentId === expense.id);
    if (children.length > 0) {
      setSplitMode(true);
      setSplitLines(children.map((c) => ({
        propertyId: c.propertyId ? String(c.propertyId) : "",
        category: c.category,
        amount: c.amount,
        notes: c.notes || "",
      })));
    } else {
      setSplitMode(false);
      setSplitLines([]);
    }
    setShowAdd(true);
  }

  function openDuplicate(expense: Expense) {
    setEditId(null);
    setIsDuplicating(true);
    setForm({
      propertyId: (expense as any).isCompanyLevel ? "__company__" : (expense.propertyId ? String(expense.propertyId) : ""),
      category: expense.category,
      description: expense.description || "",
      amount: expense.amount,
      date: "",
      vendor: expense.vendor || "",
      notes: expense.notes || "",
    });
    setShowAdd(true);
  }

  function handleSubmit() {
    // Task #2305 — split mode: save split lines instead of a single row
    if (splitMode && editId) {
      setSplitError(null);
      if (splitLines.length < 2) {
        setSplitError("A split requires at least 2 lines."); return;
      }
      for (const l of splitLines) {
        if (!l.category || !l.amount) {
          setSplitError("Each split line must have a category and amount."); return;
        }
      }
      const parentAmount = parseFloat(form.amount || "0");
      const splitTotal = splitLines.reduce((s, l) => s + parseFloat(l.amount || "0"), 0);
      if (Math.abs(splitTotal - parentAmount) > 0.01) {
        setSplitError(`Splits must total $${parentAmount.toFixed(2)}, currently $${splitTotal.toFixed(2)}.`); return;
      }
      upsertSplitMutation.mutate({
        id: editId,
        lines: splitLines.map((l) => ({
          propertyId: l.propertyId ? parseInt(l.propertyId) : null,
          category: l.category,
          amount: l.amount,
          notes: l.notes || undefined,
        })),
      });
      return;
    }
    if (!form.category || !form.amount || !form.date) {
      toast({ title: "Missing fields", description: "Category, amount, and date are required.", variant: "destructive" });
      return;
    }
    const isCompany = form.propertyId === "__company__";
    const payload: Record<string, unknown> = {
      category: form.category,
      description: form.description || undefined,
      amount: form.amount,
      date: form.date,
      vendor: form.vendor || undefined,
      notes: form.notes || undefined,
      propertyId: isCompany ? null : (form.propertyId ? parseInt(form.propertyId) : undefined),
      isCompanyLevel: isCompany,
    };
    if (editId) {
      // Task #2314 / #3788 — if this was a split parent and the host turned the
      // split toggle off, confirm before deleting the line items. Use the
      // authoritative server child count (the cached list is property/date/
      // excluded-filtered and can undercount, silently dropping a split).
      if (editingChildCountUnresolved) return;
      if (editingActualChildCount > 0) {
        setUnsplitConfirm({ childCount: editingActualChildCount, payload });
        return;
      }
      updateMutation.mutate({ id: editId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  // Task #2314 / #3788 — proceed with the unsplit + update after the host
  // confirms. Delete the children first, THEN update, sequentially, so the
  // cache settles in a single pass and the dialog never needs a manual reload
  // (the old parallel fire-and-forget raced and left stale split rows visible).
  async function confirmUnsplitAndUpdate() {
    if (!unsplitConfirm || !editId) {
      setUnsplitConfirm(null);
      return;
    }
    const { payload } = unsplitConfirm;
    try {
      await deleteSplitMutation.mutateAsync(editId);
      await updateMutation.mutateAsync({ id: editId, data: payload });
    } finally {
      setUnsplitConfirm(null);
    }
  }

  const allExpenses = expensesQuery.data ?? [];

  // Task #3788 — authoritative child count for the unsplit-confirm guard.
  const editingChildCountQuery = useQuery<{ children: Array<{ id: number }> }>({
    queryKey: ["/api/app/expenses", editId, "splits"],
    enabled: editId != null,
  });
  const editingActualChildCount = editingChildCountQuery.data?.children?.length ?? 0;
  const editingChildCountUnresolved = editId != null && !editingChildCountQuery.isSuccess;
  const editingExpense = editId != null ? allExpenses.find((e) => e.id === editId) : undefined;

  // Task #3788 — distinct Mercury accounts (by last 4) for the account filter.
  const accountOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of allExpenses) {
      const last4 = (e as any).accountLast4 as string | null | undefined;
      const nick = (e as any).accountNickname as string | null | undefined;
      if (!last4) continue;
      map.set(last4, nick ? `${nick} (····${last4})` : `····${last4}`);
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [allExpenses]);

  const expenseDateRange = useMemo(
    () => computeDateRange(datePreset, dateFrom, dateTo),
    [datePreset, dateFrom, dateTo],
  );

  // Task #2305 — build a map from splitParentId → children array so we can
  // render indented child rows under their parent in the table.
  const splitChildrenMap = useMemo(() => {
    const m = new Map<number, typeof allExpenses>();
    for (const e of allExpenses) {
      const pid = (e as any).splitParentId as number | null;
      if (pid != null) {
        if (!m.has(pid)) m.set(pid, []);
        m.get(pid)!.push(e);
      }
    }
    return m;
  }, [allExpenses]);
  const splitParentIds = useMemo(() => new Set(splitChildrenMap.keys()), [splitChildrenMap]);

  // Task #2305 — filtered split children: apply active property/category filters
  // so only child lines that match the current view appear under each parent row,
  // and filtered totals reflect only the matching child amounts.
  const filteredSplitChildrenMap = useMemo(() => {
    const m = new Map<number, typeof allExpenses>();
    splitChildrenMap.forEach((children, parentId) => {
      let filtered = children;
      if (propertyFilter !== "all") {
        const selectedId = parseInt(propertyFilter);
        const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
        const allIds = [selectedId, ...childIds];
        filtered = filtered.filter((c) => c.propertyId !== null && allIds.includes(c.propertyId));
      }
      if (categoryFilter !== "all") {
        filtered = filtered.filter((c) => c.category === categoryFilter);
      }
      m.set(parentId, filtered);
    });
    return m;
  }, [splitChildrenMap, propertyFilter, categoryFilter, properties]);

  const filteredExpenses = useMemo(() => {
    return allExpenses.filter((e) => {
      // Task #2305 — split children never appear as standalone rows; they're
      // rendered indented under their parent.
      if ((e as any).splitParentId != null) return false;
      if (splitParentIds.has(e.id)) {
        // Task #2305 — for split parents, require at least one child matching ALL
        // active filters simultaneously (not "any child for property" + "any child for category").
        const children = splitChildrenMap.get(e.id) ?? [];
        let matching = children;
        if (propertyFilter !== "all") {
          const selectedId = parseInt(propertyFilter);
          const childPropIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
          const allIds = [selectedId, ...childPropIds];
          matching = matching.filter((c) => c.propertyId !== null && allIds.includes(c.propertyId));
        }
        if (categoryFilter !== "all") {
          matching = matching.filter((c) => c.category === categoryFilter);
        }
        if (matching.length === 0) return false;
      } else {
        if (propertyFilter !== "all") {
          const selectedId = parseInt(propertyFilter);
          const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
          const allIds = [selectedId, ...childIds];
          if (e.propertyId === null || !allIds.includes(e.propertyId)) return false;
        }
        if (categoryFilter !== "all") {
          if (e.category !== categoryFilter) return false;
        }
      }
      if (sourceFilter === "mercury" && !isMercurySource(e.externalSource)) return false;
      if (sourceFilter === "stripe_bank" && e.externalSource !== "stripe_bank") return false;
      if (sourceFilter === "manual" && e.externalSource) return false;
      // Task #2177 — scope to AI auto-categorized rows so hosts can audit
      // and bulk-revert Gemini's guesses in one place.
      if (sourceFilter === "ai_auto" && (e as any).categorySource !== "ai_auto") return false;
      // Task #3788 — account (last-4) filter.
      if (accountFilter !== "all" && ((e as any).accountLast4 ?? "") !== accountFilter) return false;
      if (expenseDateRange.from && e.date < expenseDateRange.from) return false;
      if (expenseDateRange.to && e.date > expenseDateRange.to) return false;
      return true;
    });
  }, [allExpenses, propertyFilter, categoryFilter, sourceFilter, accountFilter, properties, expenseDateRange, splitParentIds, splitChildrenMap]);

  const searchFilteredExpenses = useMemo(() => {
    const q = expenseSearchText.trim().toLowerCase();
    if (!q) return filteredExpenses;
    return filteredExpenses.filter((e) => {
      const desc = (e.description || "").toLowerCase();
      const vendor = (e.vendor || "").toLowerCase();
      return desc.includes(q) || vendor.includes(q);
    });
  }, [filteredExpenses, expenseSearchText]);

  // Revenue triage — bucket rows AFTER the search/property/date/category/
  // source/account filters so the tab counts reflect the current filters.
  const rowReviewStatus = (e: ExpenseWithDoc) =>
    deriveReviewStatus({
      reviewedAt: (e as any).reviewedAt,
      excludedAt: (e as any).excludedAt,
      voidedAt: e.voidedAt,
      externalSource: e.externalSource,
    });
  const statusCounts = useMemo(() => {
    const counts: Record<ReviewStatusFilter, number> = {
      for_review: 0,
      categorized: 0,
      excluded: 0,
      all: searchFilteredExpenses.length,
    };
    for (const e of searchFilteredExpenses) counts[rowReviewStatus(e)]++;
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilteredExpenses]);
  const statusFilteredExpenses = useMemo(
    () =>
      statusFilter === "all"
        ? searchFilteredExpenses
        : searchFilteredExpenses.filter((e) => rowReviewStatus(e) === statusFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFilteredExpenses, statusFilter],
  );

  // Voided/excluded rows never count toward the money total, matching the
  // pre-triage behavior where they were hidden by default.
  const nonVoidedFiltered = useMemo(() => statusFilteredExpenses.filter((e) => !e.voidedAt && !(e as any).excludedAt), [statusFilteredExpenses]);

  // Task #2146 — auto-suggest categories for uncategorized expenses based on
  // the host's prior categorizations of the same vendor. For each normalized
  // vendor name we tally up the categories used on non-"other", non-voided,
  // non-excluded expenses and pick the most common one (ties broken by most
  // recent use). The suggestion is only surfaced when there is at least one
  // prior categorization, so a one-click "Apply" can replace opening the
  // dropdown for hosts cleaning up large Mercury imports.
  const vendorSuggestionMap = useMemo(() => {
    const tally = new Map<string, Map<string, { count: number; lastUsed: string }>>();
    for (const e of allExpenses) {
      if (e.category === "other" || !e.category) continue;
      if (e.voidedAt || (e as any).excludedAt) continue;
      const key = (e.vendor || "").toLowerCase().replace(/\s+/g, " ").trim();
      if (!key) continue;
      let perCat = tally.get(key);
      if (!perCat) { perCat = new Map(); tally.set(key, perCat); }
      const prev = perCat.get(e.category);
      const dateStr = e.date || "";
      if (!prev) perCat.set(e.category, { count: 1, lastUsed: dateStr });
      else {
        prev.count += 1;
        if (dateStr > prev.lastUsed) prev.lastUsed = dateStr;
      }
    }
    const out = new Map<string, string>();
    for (const [vendor, perCat] of tally.entries()) {
      let best: { cat: string; count: number; lastUsed: string } | null = null;
      for (const [cat, info] of perCat.entries()) {
        if (
          !best ||
          info.count > best.count ||
          (info.count === best.count && info.lastUsed > best.lastUsed)
        ) {
          best = { cat, count: info.count, lastUsed: info.lastUsed };
        }
      }
      if (best) out.set(vendor, best.cat);
    }
    return out;
  }, [allExpenses]);

  const getCategorySuggestion = (expense: ExpenseWithDoc): string | null => {
    if (expense.category !== "other") return null;
    if (expense.voidedAt || (expense as any).excludedAt) return null;
    const key = (expense.vendor || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!key) return null;
    return vendorSuggestionMap.get(key) ?? null;
  };

  // Task #2159 — AI-backed category suggestions for brand-new vendors that
  // have no prior categorization history. We only ask Gemini for rows the
  // history-based map can't handle, and only confidence ≥ the host's
  // configured threshold (Task #2165, default 85) is surfaced so the host
  // trusts the pill. Server-side caching means re-renders don't re-spend
  // tokens.
  const aiSuggestionConfidenceThreshold =
    (catSettings?.aiSuggestionConfidenceThreshold ?? DEFAULT_AI_SUGGESTION_CONFIDENCE) / 100;
  const aiSuggestionCandidateIds = useMemo(() => {
    const ids: number[] = [];
    for (const e of allExpenses) {
      if (e.category !== "other") continue;
      if (e.voidedAt || (e as any).excludedAt) continue;
      if (!(e.vendor || "").trim()) continue;
      if (getCategorySuggestion(e)) continue; // history covers it
      ids.push(e.id);
      if (ids.length >= 100) break;
    }
    return ids;
  }, [allExpenses, vendorSuggestionMap]);
  const aiSuggestionPayload = useMemo(
    () => ({ expenseIds: aiSuggestionCandidateIds, categories: availableCategories }),
    [aiSuggestionCandidateIds, availableCategories],
  );
  const aiSuggestionsQuery = useQuery<{
    suggestions: Record<string, { suggestedCategory: string; confidence: number }>;
  }>({
    queryKey: ["/api/app/expenses/ai-category-suggestions", aiSuggestionPayload],
    queryFn: async () => {
      if (aiSuggestionCandidateIds.length === 0) return { suggestions: {} };
      const res = await apiRequest(
        "POST",
        "/api/app/expenses/ai-category-suggestions",
        aiSuggestionPayload,
      );
      return res.json();
    },
    enabled: aiSuggestionCandidateIds.length > 0 && availableCategories.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const getAiCategorySuggestion = (
    expense: ExpenseWithDoc,
  ): { category: string; confidence: number } | null => {
    if (expense.category !== "other") return null;
    if (expense.voidedAt || (expense as any).excludedAt) return null;
    if (!(expense.vendor || "").trim()) return null;
    const hit = aiSuggestionsQuery.data?.suggestions?.[String(expense.id)];
    if (!hit) return null;
    if (hit.confidence < aiSuggestionConfidenceThreshold) return null;
    return { category: hit.suggestedCategory, confidence: hit.confidence };
  };

  // Task #2305 — for split parents, use sum of FILTERED children only;
  // if parent is a split parent but all children were filtered out, contribute 0 (not parent amount).
  const totalAmount = nonVoidedFiltered.reduce((s, e) => {
    if (splitParentIds.has(e.id)) {
      const children = filteredSplitChildrenMap.get(e.id) ?? [];
      return s + children.reduce((cs, c) => cs + parseFloat(c.amount || "0"), 0);
    }
    return s + parseFloat(e.amount || "0");
  }, 0);
  const totalExpensesCount = statusFilteredExpenses.length;
  const safeExpensePage = clampPage(page, totalExpensesCount, pageSize);
  useEffect(() => {
    if (safeExpensePage !== page) {
      updateExpensesQuery({ page: safeExpensePage <= 1 ? null : String(safeExpensePage) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeExpensePage, page]);
  const expensesStart = (safeExpensePage - 1) * pageSize;
  const getPropertyName = (id: number | null) => {
    if (!id) return "—";
    const p = properties.find((pr) => pr.id === id);
    return p ? p.name : "—";
  };
  const sortedExpenses = useMemo(() => {
    if (!sortState) return statusFilteredExpenses;
    const { col, dir } = sortState;
    return [...statusFilteredExpenses].sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (col) {
        case "date": aVal = a.date; bVal = b.date; break;
        case "property": aVal = getPropertyName(a.propertyId); bVal = getPropertyName(b.propertyId); break;
        case "category": aVal = a.category; bVal = b.category; break;
        case "description": aVal = a.description || ""; bVal = b.description || ""; break;
        case "vendor": aVal = a.vendor || ""; bVal = b.vendor || ""; break;
        case "amount": aVal = parseFloat(a.amount || "0"); bVal = parseFloat(b.amount || "0"); break;
        case "account":
          aVal = a.accountNickname ?? (a.accountLast4 ? `····${a.accountLast4}` : "");
          bVal = b.accountNickname ?? (b.accountLast4 ? `····${b.accountLast4}` : "");
          break;
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return dir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return dir === "asc" ? cmp : -cmp;
    });
  }, [statusFilteredExpenses, sortState]);
  const pagedExpenses = useMemo(
    () => sortedExpenses.slice(expensesStart, expensesStart + pageSize),
    [sortedExpenses, expensesStart, pageSize],
  );
  const { handleItemClick: handleExpenseClick } = useRangeSelection(
    pagedExpenses.map((e) => e.id),
    selectedIds,
    setSelectedIds,
    sortState ? `${sortState.col}:${sortState.dir}:${expensesStart}` : `none:asc:${expensesStart}`,
  );
  const selectableProperties = properties.filter((p) => p.status !== "archive");

  // Revenue triage — rows selectable for bulk actions under the current tab.
  // Excluded rows stay selectable (bulk Restore lives on the Excluded tab);
  // voided rows never are.
  const selectableFilteredIds = useMemo(
    () => statusFilteredExpenses.filter((e) => !e.voidedAt).map((e) => e.id),
    [statusFilteredExpenses],
  );

  function handleSortClick(col: string) {
    setSortState((prev) => {
      if (!prev || prev.col !== col) return { col, dir: "asc" };
      if (prev.dir === "asc") return { col, dir: "desc" };
      return null;
    });
  }

  function SortIcon({ col }: { col: string }) {
    if (!sortState || sortState.col !== col) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    if (sortState.dir === "asc") return <ArrowUp className="h-3.5 w-3.5 ml-1 text-blue-600" />;
    return <ArrowDown className="h-3.5 w-3.5 ml-1 text-blue-600" />;
  }

  const bulkUpdateMutation = useMutation({
    mutationFn: async (payload: { ids: number[]; patch: { propertyId?: number | null; category?: string; vendor?: string; isCompanyLevel?: boolean } }) =>
      apiRequest("PATCH", "/api/app/expenses/bulk", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", "with-voided"] });
      toast({ title: "Updated", description: `${selectedIds.size} expense(s) updated.` });
      setSelectedIds(new Set());
      setBulkVendor("");
      setShowBulkVendor(false);
    },
    onError: (e: Error) => toast({ title: "Bulk update failed", description: parseErrorMessage(e, "Couldn't update expenses. Please try again."), variant: "destructive" }),
  });

  // Revenue triage — one-click bulk setters (QBO-style): picking a value in
  // the bulk bar applies it immediately; no separate Apply step. The server
  // stamps reviewedAt on category/property changes.
  function handleBulkSetProperty(v: string) {
    if (selectedIds.size === 0 || !v) return;
    const patch: { propertyId?: number | null; isCompanyLevel?: boolean } = {};
    if (v === "__clear__") { patch.propertyId = null; patch.isCompanyLevel = false; }
    else if (v === "__company__") { patch.isCompanyLevel = true; patch.propertyId = null; }
    else { patch.propertyId = parseInt(v); patch.isCompanyLevel = false; }
    bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), patch });
  }

  function handleBulkSetCategory(v: string) {
    if (selectedIds.size === 0 || !v) return;
    // Task #2167 — record AI override telemetry for any selected row that
    // had a high-confidence AI suggestion visible but the bulk choice
    // differs from the suggested category.
    for (const expense of allExpenses) {
      if (!selectedIds.has(expense.id)) continue;
      const ai = getAiCategorySuggestion(expense);
      if (ai && v !== ai.category) {
        recordAiSuggestionEvent(expense.id, "overridden", v);
      }
    }
    bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), patch: { category: v } });
  }

  function handleBulkSetVendor() {
    if (selectedIds.size === 0 || !bulkVendor.trim()) return;
    bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), patch: { vendor: bulkVendor.trim() } });
  }

  // Revenue triage — human "I checked this" stamp. `reviewed: true` stamps
  // reviewedAt (row moves to Categorized), `false` clears it (back to For
  // review). Used by the per-row green check and the bulk bar.
  const markReviewedMutation = useMutation({
    mutationFn: async ({ ids, reviewed }: { ids: number[]; reviewed: boolean }) =>
      apiRequest("PATCH", "/api/app/expenses/bulk", { ids, patch: { reviewed } }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        vars.ids.forEach((id) => next.delete(id));
        return next;
      });
      toast({
        title: vars.reviewed ? "Marked reviewed" : "Moved to For review",
        description: `${vars.ids.length} expense${vars.ids.length === 1 ? "" : "s"} updated.`,
      });
    },
    onError: (e: unknown) =>
      toast({ title: "Update failed", description: parseErrorMessage(e, "Couldn't update review status. Please try again."), variant: "destructive" }),
  });

  const restoreDocMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (documentId: number) => {
      const res = await apiRequest("POST", `/api/app/property-documents/${documentId}/restore`, {});
      return res.json() as Promise<{ restoredExpenseIds: number[]; restoredRenovationItemIds: number[] }>;
    },
    onSuccess: (r) => {
      toast({ title: "Restored", description: `${r.restoredExpenseIds.length} expense(s) and ${r.restoredRenovationItemIds.length} renovation item(s) restored.` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", "with-voided"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-documents"] });
    },
    onError: (e: any) => toast({ title: "Restore failed", description: parseErrorMessage(e, "Couldn't restore this document. Please try again."), variant: "destructive" }),
  });

  // Revenue triage — option lists for the inline table-cell editors.
  const propertyCellOptions = useMemo(
    () => [
      { value: "none", label: "No property" },
      { value: "__company__", label: "Company Operations" },
      ...sortPropertiesParentFirst(properties.filter((p) => p.status !== "archive")).map((p) => ({
        value: String(p.id),
        label: (p as any).parentPropertyId
          ? `└ ${p.address || p.name || `#${p.id}`}`
          : (p.address || p.name || `Property #${p.id}`),
      })),
    ],
    [properties],
  );
  const categoryCellOptions = useMemo(
    () => availableCategories.map((c) => ({ value: c.value, label: c.label })),
    [availableCategories],
  );

  return (
    <fieldset disabled={readOnly} style={{ display: "contents" }}>
    <div data-testid="expenses-tab">
      <ManageBankFeedsLink />
      {/* Revenue triage — QBO-style status tabs. Counts reflect the current
          search/property/date/category/source/account filters. */}
      <div className="mb-3 overflow-x-auto">
        <StatusTabs value={statusFilter} onChange={setStatusFilter} counts={statusCounts} idPrefix="expenses" />
      </div>

      <div className="mb-4">
        <FinancialsToolbar
          idPrefix="expenses"
          primaryFilters={
            <>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={expenseSearchText}
                  onChange={(e) => setExpenseSearchText(e.target.value)}
                  placeholder="Search description or vendor…"
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="input-search-expenses"
                />
                {expenseSearchText && (
                  <button
                    type="button"
                    onClick={() => setExpenseSearchText("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="button-clear-search-expenses"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <PropertyFilterSelect
                value={propertyFilter === "all" ? "" : propertyFilter}
                onValueChange={(v) => setPropertyFilter(v || "all")}
                properties={properties}
                testId="select-expense-property-filter"
                placeholder="All Properties"
              />
              <DateRangeFilter
                preset={datePreset}
                customFrom={dateFrom}
                customTo={dateTo}
                onPresetChange={setDatePreset}
                onCustomFromChange={setExpenseDateFrom}
                onCustomToChange={setExpenseDateTo}
              />
            </>
          }
          advancedFilters={
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Category</label>
                <CategoryFilterSelect
                  value={categoryFilter === "all" ? "" : categoryFilter}
                  onValueChange={(v) => setCategoryFilter(v || "all")}
                  categories={availableCategories}
                  testId="select-expense-category-filter"
                  placeholder="All Categories"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Source</label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-full text-sm" data-testid="select-expense-source-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="mercury">Auto-imported (Mercury)</SelectItem>
                    <SelectItem value="stripe_bank">Stripe bank feeds</SelectItem>
                    <SelectItem value="manual">Manually entered</SelectItem>
                    {/* Task #2177 — quick filter for AI auto-categorized rows. */}
                    <SelectItem value="ai_auto">AI auto-categorized</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Task #3788 — filter by Mercury account (last 4). */}
              {accountOptions.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Mercury account</label>
                  <Select value={accountFilter} onValueChange={setAccountFilter}>
                    <SelectTrigger className="w-full text-sm" data-testid="select-expense-account-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      {accountOptions.map((a) => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm text-gray-600 cursor-pointer" onClick={() => setShowDisabledMercury(!showDisabledMercury)}>
                  Show hidden accounts
                </label>
                <Switch
                  checked={showDisabledMercury}
                  onCheckedChange={setShowDisabledMercury}
                  data-testid="switch-show-disabled-mercury-expenses"
                />
              </div>
            </div>
          }
          activeFilterCount={
            [categoryFilter !== "all", sourceFilter !== "all", accountFilter !== "all", showDisabledMercury].filter(Boolean).length
          }
          onClearFilters={() => {
            updateExpensesQuery({ category: null, source: null, disabledMercury: null, page: null });
            setAccountFilter("all");
          }}
          primaryAction={
            <Button size="sm" className="flex items-center gap-1.5" onClick={() => { resetForm(); setEditId(null); setIsDuplicating(false); setShowAdd(true); }} data-testid="button-add-expense" disabled={readOnly}>
              <Plus className="h-4 w-4" />
              Add Expense
            </Button>
          }
          overflowItems={
            <>
              <DropdownMenuItem
                disabled={readOnly}
                onClick={() => {
                  const propId = propertyFilter !== "all" ? parseInt(propertyFilter) : (properties[0]?.id);
                  if (!propId) { toast({ title: "Select a property first", description: "Choose a specific property to upload a document.", variant: "destructive" }); return; }
                  setUploadPropertyId(propId);
                  setShowUpload(true);
                }}
                data-testid="button-upload-expense"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyLink} data-testid="button-copy-expenses-link">
                <Link2 className="h-4 w-4 mr-2" />
                Copy link
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowCategoryRules(true)} data-testid="button-open-category-rules">
                <ListChecks className="h-4 w-4 mr-2" />
                Category rules
              </DropdownMenuItem>
            </>
          }
        />
      </div>

      <MercuryMergeCandidatesPanel properties={properties} />

      <HostAiSuggestionAccuracyWidget />

      {(() => {
        if (propertyFilter === "all") return null;
        const selectedId = parseInt(propertyFilter);
        const childProps = properties.filter((p) => p.parentPropertyId === selectedId);
        if (childProps.length === 0) return null;
        return (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-sm" data-testid="banner-expenses-rollup">
            <Building2 className="h-4 w-4 text-blue-500 shrink-0" />
            <span className="text-blue-800 dark:text-blue-200">
              Including expenses from {childProps.length} child unit{childProps.length > 1 ? "s" : ""}: {childProps.map((c) => c.name).join(", ")}
            </span>
          </div>
        );
      })()}

      <div className="mb-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Total Expenses</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="text-total-expenses">{fmtCurrency(totalAmount)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Count</p>
          <p className="text-lg font-semibold text-gray-700">{statusFilteredExpenses.length}</p>
        </div>
      </div>

      {!readOnly && (() => {
        // Task #2135 — When the selection includes ≥1 non-excluded Mercury
        // row, surface an "Exclude" action. Mercury rows aren't deletable;
        // exclude is the host-facing "hide from active list" verb.
        const mercuryIdsInSelection = statusFilteredExpenses
          .filter((e) => selectedIds.has(e.id) && isImportedBankSource(e.externalSource) && rowReviewStatus(e) !== "excluded")
          .map((e) => e.id);
        // Task #2147 — bulk Restore for excluded rows (Excluded tab).
        const excludedIdsInSelection = statusFilteredExpenses
          .filter((e) => selectedIds.has(e.id) && rowReviewStatus(e) === "excluded")
          .map((e) => e.id);
        // Task #2177 — bulk revert AI auto-categorized rows back to "other".
        const aiAutoIdsInSelection = statusFilteredExpenses
          .filter(
            (e) =>
              selectedIds.has(e.id) &&
              (e as any).categorySource === "ai_auto" &&
              !e.voidedAt &&
              !(e as any).excludedAt,
          )
          .map((e) => e.id);
        return (
          <BulkBar
            count={selectedIds.size}
            totalMatching={selectableFilteredIds.length}
            onSelectAllMatching={() => setSelectedIds(new Set(selectableFilteredIds))}
            onClear={() => { setSelectedIds(new Set()); setBulkVendor(""); }}
          >
            <Select value="" onValueChange={handleBulkSetProperty} disabled={bulkUpdateMutation.isPending}>
              <SelectTrigger className="w-40 text-sm h-8" data-testid="select-bulk-property">
                <SelectValue placeholder="Set property…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">— Clear property —</SelectItem>
                {/* Task #3788 — pinned company-level option for bulk set. */}
                <SelectItem value="__company__">Company Operations</SelectItem>
                {selectableProperties.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value="" onValueChange={handleBulkSetCategory} disabled={bulkUpdateMutation.isPending}>
              <SelectTrigger className="w-40 text-sm h-8" data-testid="select-bulk-category">
                <SelectValue placeholder="Set category…" />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {statusFilter === "for_review" && (
              <Button
                size="sm"
                className="h-8 whitespace-nowrap bg-green-600 hover:bg-green-700 text-white"
                onClick={() => markReviewedMutation.mutate({ ids: Array.from(selectedIds), reviewed: true })}
                disabled={markReviewedMutation.isPending}
                data-testid="button-bulk-mark-reviewed"
              >
                {markReviewedMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Mark reviewed
              </Button>
            )}
            {statusFilter === "categorized" && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 whitespace-nowrap"
                onClick={() => markReviewedMutation.mutate({ ids: Array.from(selectedIds), reviewed: false })}
                disabled={markReviewedMutation.isPending}
                data-testid="button-bulk-mark-unreviewed"
              >
                {markReviewedMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Undo2 className="h-3.5 w-3.5 mr-1" />}
                Mark unreviewed
              </Button>
            )}
            {mercuryIdsInSelection.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 whitespace-nowrap border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                onClick={() => bulkExcludeMutation.mutate(mercuryIdsInSelection)}
                disabled={bulkExcludeMutation.isPending}
                data-testid="button-bulk-exclude"
              >
                <EyeOff className="h-3.5 w-3.5 mr-1" />
                {bulkExcludeMutation.isPending ? "Excluding…" : `Exclude ${mercuryIdsInSelection.length} bank transactions`}
              </Button>
            )}
            {excludedIdsInSelection.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 whitespace-nowrap border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/50"
                onClick={() => bulkUnexcludeMutation.mutate(excludedIdsInSelection)}
                disabled={bulkUnexcludeMutation.isPending}
                data-testid="button-bulk-unexclude"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />
                {bulkUnexcludeMutation.isPending ? "Restoring…" : `Restore ${excludedIdsInSelection.length}`}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2" aria-label="More bulk actions" data-testid="button-bulk-overflow">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowBulkVendor(true)} data-testid="button-bulk-set-vendor">
                  <Pencil className="h-4 w-4 mr-2" />
                  Set vendor…
                </DropdownMenuItem>
                {aiAutoIdsInSelection.length > 0 && (
                  <DropdownMenuItem
                    disabled={bulkUpdateMutation.isPending}
                    onClick={() => bulkUpdateMutation.mutate({ ids: aiAutoIdsInSelection, patch: { category: "other" } })}
                    data-testid="button-bulk-revert-ai"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Revert {aiAutoIdsInSelection.length} AI {aiAutoIdsInSelection.length === 1 ? "guess" : "guesses"}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </BulkBar>
        );
      })()}

      <div className="border border-gray-200 rounded-lg overflow-x-auto" data-testid="expenses-scroll-region" tabIndex={0} role="region" aria-label="Expenses table">
        {expensesQuery.isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <table className="w-full min-w-[1000px] text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-gray-200 bg-white">
                {!readOnly && (
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={pagedExpenses.length > 0 && pagedExpenses.filter(e => !e.voidedAt).every(e => selectedIds.has(e.id))}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            pagedExpenses.filter(e => !e.voidedAt).forEach(e => next.add(e.id));
                            return next;
                          });
                        } else {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            pagedExpenses.forEach(e => next.delete(e.id));
                            return next;
                          });
                        }
                      }}
                      data-testid="checkbox-select-all-expenses"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("date")} data-testid="th-sort-date">
                  <span className="flex items-center">Date<SortIcon col="date" /></span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("property")} data-testid="th-sort-property">
                  <span className="flex items-center">Property<SortIcon col="property" /></span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("category")} data-testid="th-sort-category">
                  <span className="flex items-center">Category<SortIcon col="category" /></span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("description")} data-testid="th-sort-description">
                  <span className="flex items-center">Description<SortIcon col="description" /></span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("vendor")} data-testid="th-sort-vendor">
                  <span className="flex items-center">Vendor<SortIcon col="vendor" /></span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("account")} data-testid="th-sort-account">
                  <span className="flex items-center">Account<SortIcon col="account" /></span>
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSortClick("amount")} data-testid="th-sort-amount">
                  <span className="flex items-center justify-end">Amount<SortIcon col="amount" /></span>
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {searchFilteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={readOnly ? 8 : 9} className="px-4 py-6" data-testid="text-no-expenses">
                    {allExpenses.length === 0 ? (
                      <JourneyEmptyState
                        page="financials"
                        stage={journeyStage}
                        icon={<Plus className="h-10 w-10 text-gray-300" />}
                        primaryAction={{ onClick: () => { resetForm(); setEditId(null); setIsDuplicating(false); setShowAdd(true); } }}
                      />
                    ) : expenseSearchText.trim() ? (
                      <div className="text-center py-6 text-gray-400" data-testid="text-no-expenses-search">No transactions match your search.</div>
                    ) : (
                      <div className="text-center py-6 text-gray-400">No expenses match your filters.</div>
                    )}
                  </td>
                </tr>
              ) : (
                pagedExpenses.map((expense) => {
                  const isVoided = !!expense.voidedAt;
                  const isExcluded = rowReviewStatus(expense) === "excluded";
                  const isSelected = selectedIds.has(expense.id);
                  // Task #2305 — only show children that match active property/category filters
                  const rowSplitChildren = filteredSplitChildrenMap.get(expense.id) ?? [];
                  return (
                  <Fragment key={expense.id}>
                  <tr
                    className={`border-b border-gray-100${isSelected ? " bg-blue-50 dark:bg-blue-950/40" : ""}${!isSelected && !isVoided && !isExcluded && expense.documentId ? " hover:bg-gray-50 cursor-pointer" : ""}${isVoided ? " bg-gray-50 text-gray-400" : ""}${isExcluded ? " bg-gray-50 opacity-60" : ""}`}
                    data-testid={`row-expense-${expense.id}`}
                    onClick={!isVoided && !isExcluded && expense.documentId && !isSelected ? () => window.open(`/api/app/property-documents/${expense.documentId}/file`, "_blank") : undefined}
                  >
                    {!readOnly && (
                      <td className="px-3 py-3 w-10" onClick={(e) => { e.stopPropagation(); if (!isVoided) handleExpenseClick(expense.id, e.shiftKey); }}>
                        {/* Task #2147 — excluded rows stay selectable so hosts
                            can bulk-restore from the Excluded tab. */}
                        {!isVoided && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => {}}
                            data-testid={`checkbox-expense-${expense.id}`}
                          />
                        )}
                      </td>
                    )}
                    <td className={`px-4 py-3 whitespace-nowrap ${isVoided || isExcluded ? "line-through text-gray-400" : "text-gray-700"}`}>{expense.date}</td>
                    <td className={`px-4 py-3 ${isVoided ? "line-through" : "text-gray-700"}`}>
                      {(isVoided || isExcluded || readOnly) ? (
                        <div className="flex flex-col items-start gap-0.5">
                          {(expense as any).isCompanyLevel ? (
                            // Task #3788 — company-level (non-property) expense marker.
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                              data-testid={`text-company-level-${expense.id}`}
                            >
                              <Building2 className="h-3 w-3" />
                              Company Operations
                            </span>
                          ) : (
                            <span>{expense.propertyId ? getPropertyName(expense.propertyId) : "—"}</span>
                          )}
                          {isMercurySource(expense.externalSource) && (expense.accountNickname || expense.cardLast4) && (
                            <span className="text-[10px] text-gray-500" data-testid={`text-mercury-account-${expense.id}`}>
                              {expense.accountNickname && expense.cardLast4
                                ? `${expense.accountNickname} · ${expense.cardLast4}`
                                : expense.accountNickname || `Card · ${expense.cardLast4}`}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-start gap-0.5" onClick={(e) => e.stopPropagation()}>
                          {/* Revenue triage \u2014 inline property picker. The
                              single-row PATCH stamps reviewedAt server-side. */}
                          <InlineCellEditor
                            value={(expense as any).isCompanyLevel ? "__company__" : expense.propertyId ? String(expense.propertyId) : null}
                            placeholder="Add property"
                            options={propertyCellOptions}
                            onSelect={(v) => {
                              if (v === "__company__") {
                                updateMutation.mutate({ id: expense.id, data: { isCompanyLevel: true, propertyId: null } });
                              } else {
                                updateMutation.mutate({ id: expense.id, data: { isCompanyLevel: false, propertyId: v === "none" ? null : Number(v) } });
                              }
                            }}
                            testId={`select-expense-property-${expense.id}`}
                            disabled={updateMutation.isPending}
                          />
                          {/* Task #2131 — Mercury account info remains visible below the select */}
                          {isMercurySource(expense.externalSource) && (expense.accountNickname || expense.cardLast4) && (
                            <span className="text-[10px] text-gray-500 pl-1" data-testid={`text-mercury-account-${expense.id}`}>
                              {expense.accountNickname && expense.cardLast4
                                ? `${expense.accountNickname} · ${expense.cardLast4}`
                                : expense.accountNickname || `Card · ${expense.cardLast4}`}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className={`flex items-center gap-1.5 flex-wrap ${isVoided || isExcluded ? "line-through" : ""}`}>
                        {(() => {
                          // Task #2146 — auto-suggested category pill for
                          // uncategorized rows based on the host's prior
                          // categorization of the same vendor.
                          // Task #2159 — if no history match, fall back to a
                          // visually-distinct AI suggestion pill (Gemini).
                          if (readOnly || isVoided || isExcluded) return null;
                          const customCats = (catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [];
                          const historySuggested = getCategorySuggestion(expense);
                          if (historySuggested) {
                            const label = getCategoryLabel(historySuggested, customCats);
                            return (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateMutation.mutate({ id: expense.id, data: { category: historySuggested } });
                                }}
                                disabled={updateMutation.isPending}
                                title={`Apply suggested category based on prior ${expense.vendor || "vendor"} categorizations`}
                                 className="financials-suggestion financials-suggestion-history inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium border disabled:opacity-50"
                                data-testid={`button-apply-suggestion-${expense.id}`}
                              >
                                 <span>Suggested:</span>
                                <span data-testid={`text-suggested-category-${expense.id}`}>{label}</span>
                                <span className="ml-0.5 underline">Apply</span>
                              </button>
                            );
                          }
                          const aiSuggested = getAiCategorySuggestion(expense);
                          if (aiSuggested) {
                            const label = getCategoryLabel(aiSuggested.category, customCats);
                            const pct = Math.round(aiSuggested.confidence * 100);
                            return (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  recordAiSuggestionEvent(expense.id, "accepted");
                                  updateMutation.mutate({ id: expense.id, data: { category: aiSuggested.category } });
                                }}
                                disabled={updateMutation.isPending}
                                title={`AI-suggested category for ${expense.vendor || "this vendor"} (${pct}% confidence). Click Apply to accept.`}
                                 className="financials-suggestion financials-suggestion-ai inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium border disabled:opacity-50"
                                data-testid={`button-apply-ai-suggestion-${expense.id}`}
                              >
                                <Sparkles className="h-3 w-3" />
                                 <span>AI:</span>
                                <span data-testid={`text-ai-suggested-category-${expense.id}`}>{label}</span>
                                <span className="ml-0.5 underline">Apply</span>
                              </button>
                            );
                          }
                          return null;
                        })()}
                        {!readOnly && !isVoided && !isExcluded ? (
                          // Task #2129 / revenue triage — inline category
                          // editing (searchable picker) so hosts categorize
                          // in place; the PATCH stamps reviewedAt server-side.
                          <span onClick={(e) => e.stopPropagation()}>
                            <InlineCellEditor
                              value={expense.category}
                              displayLabel={getCategoryLabel(expense.category, (catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [])}
                              placeholder="Categorize"
                              options={categoryCellOptions}
                              onSelect={(v) => {
                                if (v && v !== expense.category) {
                                  // Task #2167 — if a high-confidence AI suggestion
                                  // was visible on this row and the host picked a
                                  // different category, record the override.
                                  const ai = getAiCategorySuggestion(expense);
                                  if (ai && v !== ai.category) {
                                    recordAiSuggestionEvent(expense.id, "overridden", v);
                                  }
                                  updateMutation.mutate({ id: expense.id, data: { category: v } });
                                }
                              }}
                              testId={`select-inline-category-${expense.id}`}
                              disabled={updateMutation.isPending}
                            />
                          </span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isVoided || isExcluded ? "bg-gray-100 text-gray-400" : "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"}`}>
                            {getCategoryLabel(expense.category, (catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [])}
                          </span>
                        )}
                        {/* Task #2305 — Split badge on parent rows.
                            Task #2328 — Added tooltip explaining that the
                            parent shows the full bank amount while children
                            carry the per-category / per-property breakdown. */}
                        {splitParentIds.has(expense.id) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center gap-0.5 cursor-help"
                                  data-testid={`badge-split-${expense.id}`}
                                >
                                  <Scissors className="h-2.5 w-2.5" />
                                  Split
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs max-w-[260px]">
                                  This row shows the full bank amount. The per-category and per-property breakdown is on the child rows below.
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {isVoided && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-500" data-testid={`badge-voided-${expense.id}`}>Voided</span>
                        )}
                        {isExcluded && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-500" data-testid={`badge-excluded-${expense.id}`}>Excluded</span>
                        )}
                        {(expense as any).accountIncludedInPnl === false && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-testid={`badge-hidden-from-pnl-${expense.id}`}>Hidden from P&L</span>
                        )}
                        <ExpenseSourceDocumentTooltip
                          expenseId={expense.id}
                          documentId={expense.documentId}
                          documentFileName={expense.documentFileName}
                        />
                        {/* Task #2177 — flag rows that were auto-categorized by
                            Gemini so hosts can spot AI guesses at a glance and
                            click through to the AI auto-categorized filter. */}
                        {(expense as any).categorySource === "ai_auto" && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSourceFilter("ai_auto");
                                  }}
                                  className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 flex items-center gap-0.5 hover:bg-violet-100"
                                  data-testid={`badge-ai-auto-${expense.id}`}
                                >
                                  <Sparkles className="h-2.5 w-2.5" />
                                  AI
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs max-w-[240px]">
                                  Category was auto-assigned by AI on import. Click to review all AI-categorized expenses, or edit the category to clear this tag.
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {isImportedBankSource(expense.externalSource) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-0.5"
                                  data-testid={`badge-mercury-${expense.id}`}
                                >
                                  <Zap className="h-2.5 w-2.5" />
                                  {expense.externalSource === "stripe_bank" ? "Stripe bank feed" : expense.externalSource === "mercury_card" ? "Mercury Card" : "Mercury"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  <div>
                                    {expense.externalSource === "stripe_bank"
                                      ? "Imported through Stripe bank feeds. Review transfers and duplicates before restoring to P&L."
                                      : expense.externalSource === "mercury_card"
                                      ? "Auto-imported from Mercury IO Credit Card"
                                      : "Auto-imported from Mercury Bank"}
                                  </div>
                                  {expense.accountNickname && (
                                    <div className="text-[10px] mt-0.5 opacity-80">
                                      Account: {expense.accountNickname}
                                    </div>
                                  )}
                                  {expense.externalId && (
                                    <div className="font-mono text-[10px] mt-0.5 opacity-80">
                                      Txn: {expense.externalId}
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${isVoided || isExcluded ? "line-through text-gray-400" : "text-gray-700"}`}>{expense.description ? highlightMatch(expense.description, expenseSearchText) : "—"}</td>
                    <td className={`px-4 py-3 ${isVoided || isExcluded ? "line-through text-gray-400" : "text-gray-700"}`}>{expense.vendor ? highlightMatch(expense.vendor, expenseSearchText) : "—"}</td>
                    <td className={`px-4 py-3 ${isVoided || isExcluded ? "line-through text-gray-400" : "text-gray-500"}`} data-testid={`text-account-${expense.id}`}>
                      {isImportedBankSource(expense.externalSource)
                        ? (formatMercuryAccountLabel(expense.accountNickname, expense.accountLast4) ?? "—")
                        : "—"}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${isVoided || isExcluded ? "line-through text-gray-400" : "text-gray-900"}`}>{fmtCurrency(parseFloat(expense.amount || "0"))}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {isVoided ? (
                          expense.documentId ? (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setManageDocId(expense.documentId as number); }}
                                className="text-gray-400 hover:text-orange-600 p-1"
                                title="Manage source document"
                                data-testid={`button-manage-document-${expense.id}`}
                              >
                                <FileSearch className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); restoreDocMutation.mutate(expense.documentId as number); }}
                                disabled={restoreDocMutation.isPending}
                                className="text-gray-400 hover:text-green-600 p-1 disabled:opacity-50"
                                title="Restore source document and linked records"
                                data-testid={`button-restore-expense-${expense.id}`}
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )
                        ) : isExcluded ? (
                          !readOnly ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); unexcludeMutation.mutate(expense.id); }}
                                    disabled={unexcludeMutation.isPending}
                                    className="text-gray-400 hover:text-green-600 p-1 disabled:opacity-50"
                                    data-testid={`button-unexclude-expense-${expense.id}`}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Restore to active expenses</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )
                        ) : (
                          <>
                            {/* Revenue triage — one-click human confirmation.
                                Only surfaced in the For review queue. */}
                            {!readOnly && statusFilter === "for_review" && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); markReviewedMutation.mutate({ ids: [expense.id], reviewed: true }); }}
                                      disabled={markReviewedMutation.isPending}
                                      className="text-gray-400 hover:text-green-600 p-1 disabled:opacity-50"
                                      data-testid={`button-mark-reviewed-${expense.id}`}
                                      aria-label="Mark reviewed"
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>Mark reviewed</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Revenue triage — secondary row actions collapsed
                                into one ⋯ menu; every action keeps its testid. */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-gray-400 hover:text-gray-600 p-1"
                                  aria-label="Row actions"
                                  data-testid={`button-expense-actions-${expense.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {!readOnly && (
                                  <DropdownMenuItem onClick={() => openEdit(expense)} data-testid={`button-edit-expense-${expense.id}`}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit / Split
                                  </DropdownMenuItem>
                                )}
                                {!readOnly && !isImportedBankSource(expense.externalSource) && (
                                  <DropdownMenuItem onClick={() => openDuplicate(expense)} data-testid={`button-duplicate-expense-${expense.id}`}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                )}
                                {!readOnly && expense.documentId && (
                                  <DropdownMenuItem onClick={() => setManageDocId(expense.documentId as number)} data-testid={`button-manage-document-${expense.id}`}>
                                    <FileSearch className="h-4 w-4 mr-2" />
                                    Manage source document
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setAttachmentExpense(expense)} data-testid={`button-attachments-${expense.id}`}>
                                  <Paperclip className="h-4 w-4 mr-2" />
                                  Attachments
                                </DropdownMenuItem>
                                {!readOnly && isImportedBankSource(expense.externalSource) && (
                                  <DropdownMenuItem
                                    disabled={excludeMutation.isPending}
                                    onClick={() => excludeMutation.mutate(expense.id)}
                                    data-testid={`button-exclude-expense-${expense.id}`}
                                  >
                                    <EyeOff className="h-4 w-4 mr-2" />
                                    Exclude from expenses
                                  </DropdownMenuItem>
                                )}
                                {!readOnly && !isImportedBankSource(expense.externalSource) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-red-600 focus:text-red-600"
                                      onClick={() => deleteMutation.mutate(expense.id)}
                                      data-testid={`button-delete-expense-${expense.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Task #2305 — indented child rows for split expenses */}
                  {rowSplitChildren.map((child) => (
                    <tr key={child.id} className="bg-indigo-50/30 dark:bg-indigo-950/10 border-l-4 border-indigo-200 dark:border-indigo-800" data-testid={`row-expense-child-${child.id}`}>
                      {!readOnly && <td className="px-3 py-2 w-10" />}
                      <td className="px-4 py-2 pl-10 text-xs text-gray-500 whitespace-nowrap">{child.date}</td>
                      <td className="px-4 py-2 text-xs text-gray-600">{child.propertyId ? getPropertyName(child.propertyId) : <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">
                          {getCategoryLabel(child.category, (catSettings?.customCategories as {label: string, value: string}[]) || [])}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{child.description || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{child.vendor || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2 text-xs text-gray-400">—</td>
                      <td className="px-4 py-2 text-right text-xs font-medium text-gray-700">{fmtCurrency(parseFloat(child.amount || "0"))}</td>
                      <td className="px-4 py-2">
                        {!readOnly && (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); openEdit(expense); }}
                              className="text-gray-400 hover:text-gray-600 p-1"
                              title="Edit split"
                              data-testid={`button-edit-split-child-${child.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalExpensesCount > 0 && (
        <div className="mt-3">
          <PaginationControls
            page={safeExpensePage}
            pageSize={pageSize}
            total={totalExpensesCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testIdPrefix="expenses"
            itemLabel="expenses"
          />
        </div>
      )}

      {showUpload && uploadPropertyId && (
        <DocumentUploadDialog
          open={showUpload}
          onClose={() => setShowUpload(false)}
          propertyId={uploadPropertyId}
          mode="expense"
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] })}
        />
      )}

      {manageDocId && (
        <ManageDocumentDialog
          open={manageDocId !== null}
          onClose={() => setManageDocId(null)}
          documentId={manageDocId}
        />
      )}

      {attachmentExpense && (
        <ExpenseAttachmentDrawer
          open={attachmentExpense !== null}
          onClose={() => setAttachmentExpense(null)}
          expense={attachmentExpense}
          propertyId={attachmentExpense.propertyId ?? null}
          readOnly={readOnly}
        />
      )}

      {/* Revenue triage — sticky (property, merchant) → category rules viewer,
          opened from the toolbar overflow menu. */}
      <CategoryRulesDialog
        open={showCategoryRules}
        onOpenChange={setShowCategoryRules}
        customCategories={(catSettings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || []}
      />

      {/* Revenue triage — bulk "Set vendor" moved out of the bar into a small
          dialog reached from the bulk overflow menu. Dismissal is gated on the
          in-flight PATCH so a stray click can't drop the result. */}
      <Dialog
        open={showBulkVendor}
        onOpenChange={(v) => {
          if (bulkUpdateMutation.isPending) return;
          setShowBulkVendor(v);
          if (!v) setBulkVendor("");
        }}
      >
        <DialogContent
          className="sm:max-w-sm"
          data-testid="dialog-bulk-vendor"
          onInteractOutside={(e) => { if (bulkUpdateMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (bulkUpdateMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (bulkUpdateMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Set vendor for {selectedIds.size} selected expense{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Vendor name"
            value={bulkVendor}
            onChange={(e) => setBulkVendor(e.target.value)}
            data-testid="input-bulk-vendor"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowBulkVendor(false); setBulkVendor(""); }}
              disabled={bulkUpdateMutation.isPending}
              data-testid="button-bulk-vendor-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkSetVendor}
              disabled={bulkUpdateMutation.isPending || !bulkVendor.trim()}
              data-testid="button-bulk-apply"
            >
              {bulkUpdateMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Applying</> : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAdd} onOpenChange={(v) => { if (!v) { setShowAdd(false); setEditId(null); resetForm(); } }}>
        <DialogContent
          className="sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          data-testid="dialog-add-expense"
          // The Select dropdowns render into a Radix portal that sits outside
          // the Dialog DOM node. Without these guards the Dialog sees clicks
          // inside the dropdown as "outside" interactions and closes itself
          // before the host can pick an option.
          onInteractOutside={(e) => {
            const target = e.target as Element | null;
            if (target?.closest("[data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            const target = e.target as Element | null;
            if (target?.closest("[data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editId ? "Edit Expense" : isDuplicating ? "Duplicate Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          {(() => {
            const editing = editId ? allExpenses.find((e) => e.id === editId) : null;
            if (!editing || !isImportedBankSource(editing.externalSource)) return null;
            return (
              <div
                className="flex-shrink-0 flex items-start gap-2 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50 text-xs text-emerald-800"
                data-testid="banner-mercury-source"
              >
                <Zap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">
                    {editing.externalSource === "stripe_bank"
                      ? "Imported through Stripe bank feeds"
                      : editing.externalSource === "mercury_card"
                      ? "Auto-imported from Mercury IO Credit Card"
                      : "Auto-imported from Mercury Bank"}
                  </div>
                  {editing.accountNickname && (
                    <div className="text-[11px] mt-0.5 opacity-80">
                      Account: {editing.accountNickname}
                    </div>
                  )}
                  {editing.externalId && (
                    <div className="font-mono text-[11px] mt-0.5 opacity-80">
                      Transaction ID: {editing.externalId}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
              <Select value={form.propertyId} onValueChange={(v) => setForm({ ...form, propertyId: v })}>
                <SelectTrigger data-testid="select-expense-property">
                  <SelectValue placeholder="Select property (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {/* Task #3788 — pinned company-level option (no property). */}
                  <SelectItem value="__company__">Company Operations</SelectItem>
                  {selectableProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="select-expense-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {/* Task #2311 — show balance sheet categories in a separate group */}
                  {(() => {
                    const operating = availableCategories.filter((c) => !balanceSheetCategoryValues.has(c.value));
                    const bsCats = availableCategories.filter((c) => balanceSheetCategoryValues.has(c.value));
                    return (
                      <>
                        <SelectGroup>
                          <SelectLabel>Operating Expenses</SelectLabel>
                          {operating.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectGroup>
                        {bsCats.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Balance Sheet</SelectLabel>
                            {bsCats.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="input-expense-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="input-expense-date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                placeholder="Brief description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="input-expense-description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <Input
                placeholder="Vendor name"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                data-testid="input-expense-vendor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <Textarea
                placeholder="Additional notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="input-expense-notes"
              />
            </div>
            {/* Task #2305 — Split transaction editor (available only when editing) */}
            {editId && (
              <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <Scissors className="h-3.5 w-3.5 text-indigo-500" />
                      Split Transaction
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">Break into multiple line items with different properties or categories</p>
                  </div>
                  <Switch
                    checked={splitMode}
                    onCheckedChange={(v) => {
                      setSplitMode(v);
                      if (v && splitLines.length === 0) {
                        const half = (parseFloat(form.amount || "0") / 2).toFixed(2);
                        setSplitLines([
                          { propertyId: form.propertyId === "__company__" ? "" : form.propertyId, category: form.category, amount: half, notes: "" },
                          { propertyId: "", category: form.category, amount: half, notes: "" },
                        ]);
                      } else if (!v) {
                        setSplitLines([]);
                        setSplitError(null);
                      }
                    }}
                    data-testid="switch-split-mode"
                  />
                </div>
                {splitMode && (
                  <div className="space-y-2">
                    {/* Task #2305 — inline blocking error message */}
                    {splitError && (
                      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 font-medium" data-testid="text-split-error">
                        {splitError}
                      </div>
                    )}
                    {(() => {
                      const total = splitLines.reduce((s, l) => s + parseFloat(l.amount || "0"), 0);
                      const parentAmt = parseFloat(form.amount || "0");
                      const diff = parentAmt - total;
                      const balanced = Math.abs(diff) < 0.01;
                      return (
                        <div className={`text-xs flex justify-between px-1 ${balanced ? "text-green-600" : "text-amber-600"}`}>
                          <span>Lines total: ${total.toFixed(2)} / ${parentAmt.toFixed(2)}</span>
                          <span>{balanced ? "✓ Balanced" : diff > 0 ? `$${diff.toFixed(2)} remaining` : `$${(-diff).toFixed(2)} over`}</span>
                        </div>
                      );
                    })()}
                    {splitLines.map((line, idx) => (
                      <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-2 bg-gray-50 dark:bg-gray-900/50">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">Line {idx + 1}</span>
                          {splitLines.length > 2 && (
                            <button
                              type="button"
                              onClick={() => setSplitLines((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600 text-xs"
                              data-testid={`button-remove-split-line-${idx}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={line.propertyId || "__none"} onValueChange={(v) => setSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, propertyId: v === "__none" ? "" : v } : l))}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`select-split-property-${idx}`}>
                              <SelectValue placeholder="Property (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">No property</SelectItem>
                              {selectableProperties.map((p) => (
                                <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={line.category} onValueChange={(v) => setSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, category: v } : l))}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`select-split-category-${idx}`}>
                              <SelectValue placeholder="Category *" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCategories.map((c) => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            placeholder="Amount *"
                            value={line.amount}
                            onChange={(e) => setSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, amount: e.target.value } : l))}
                            className="h-8 text-xs"
                            data-testid={`input-split-amount-${idx}`}
                          />
                          <Input
                            placeholder="Notes (optional)"
                            value={line.notes}
                            onChange={(e) => setSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, notes: e.target.value } : l))}
                            className="h-8 text-xs"
                            data-testid={`input-split-notes-${idx}`}
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSplitLines((prev) => [...prev, { propertyId: "", category: form.category || "other", amount: "", notes: "" }])}
                      className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                      data-testid="button-add-split-line"
                    >
                      + Add another line
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            {/* Task #3788 — Exclude moved into the Edit view (Mercury rows only);
                the inline per-row Exclude was removed. */}
            {editingExpense && isImportedBankSource(editingExpense.externalSource) && rowReviewStatus(editingExpense) !== "excluded" && (
              <Button
                variant="outline"
                className="mr-auto text-amber-700 hover:text-amber-800 dark:text-amber-300"
                onClick={() => {
                  if (editId == null) return;
                  excludeMutation.mutate(editId, {
                    onSuccess: () => { setShowAdd(false); setEditId(null); resetForm(); },
                  });
                }}
                disabled={excludeMutation.isPending}
                data-testid="button-exclude-expense-edit"
              >
                <EyeOff className="h-4 w-4 mr-1" />
                {excludeMutation.isPending ? "Excluding…" : "Exclude from expenses"}
              </Button>
            )}
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditId(null); resetForm(); }} data-testid="button-cancel-expense">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending || upsertSplitMutation.isPending || editingChildCountUnresolved} data-testid="button-save-expense">
              {(createMutation.isPending || updateMutation.isPending || upsertSplitMutation.isPending) ? "Saving..." : editId ? "Update" : "Add Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #2314 — Confirm before discarding existing split line items
          when the host toggles Split OFF and saves. */}
      <AlertDialog open={!!unsplitConfirm} onOpenChange={(open) => { if (!open) setUnsplitConfirm(null); }}>
        <AlertDialogContent data-testid="dialog-confirm-unsplit-expense">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove split line items?</AlertDialogTitle>
            <AlertDialogDescription>
              {unsplitConfirm
                ? `This will permanently remove ${unsplitConfirm.childCount} split ${unsplitConfirm.childCount === 1 ? "line" : "lines"} from this expense. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-unsplit-expense-cancel">Keep split</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnsplitAndUpdate}
              data-testid="button-unsplit-expense-confirm"
            >
              Remove split lines
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </fieldset>
  );
}
